import { faker } from '@faker-js/faker';
import type {
  AiInference,
  AiPredictionRead,
  AiPreferences,
} from '@timelyapp/integration-cloud-api-spec';
import type { MemoryBlock } from '@timelyapp/integration-cloud-sdk/memory-processors/utils.memories.summary';
import { createMockAiPrediction } from '@timelyapp/integration-cloud-sdk/utils/test-mocks';
import { DateTime } from 'luxon';
import type { TagsDictionary } from 'redux/modules/tags.types';
import { type EnrichedProject, TagSettings } from 'types/project';
import debug from 'utils/debug';
import { fromISO } from 'utils/luxon-helpers';

/**
 * generates synthetic predictions from a set of memories.
 * respects the ai preferences and project data
 */
export const generateSyntheticPredictions = (options: {
  fromDateTime: DateTime;
  aiPreferences: AiPreferences;
  memoryBlocks: Array<{ from: string; to: string; note: string }>;
  enrichedProjectForUserIdMap: Record<number, EnrichedProject>;
  tagEntities: TagsDictionary;
}): AiPredictionRead[] => {
  const { fromDateTime, aiPreferences, memoryBlocks, enrichedProjectForUserIdMap, tagEntities } =
    options;

  faker.seed(fromDateTime.toJSDate().getTime());

  const unArchivedProjects = Object.values(enrichedProjectForUserIdMap)
    .flatMap(p => (p.fullProject != null ? [p.fullProject] : []))
    .filter(p => {
      // If AI won't fake a `note` (below), avoid projects that require notes
      if (!aiPreferences.suggest_note && p.required_notes) {
        return false;
      }

      // If AI won't fake tags (below), avoid projects that require tags
      if (!aiPreferences.train_tags && p.required_label_ids?.length) {
        return false;
      }

      return p.active;
    });
  if (!unArchivedProjects.length) {
    debug.template.infra('No active projects available for predictions');
    return [];
  }

  const threeProjects = faker.helpers.arrayElements(unArchivedProjects, 4);

  // Generate predictions from blocks
  const predictions: AiPredictionRead[] = memoryBlocks.map(block => {
    const randomProject = faker.helpers.arrayElement(threeProjects);
    const note = block.note;

    const availableLabelsIds =
      randomProject.enable_labels === TagSettings.All
        ? Object.values(tagEntities)
            .filter(tag => tag.active)
            .map(tag => tag.id)
        : randomProject.labels
            .filter(l => tagEntities[l.label_id]?.active === true)
            .map(l => l.label_id);
    const sampleLabelIds = randomProject.required_label_ids?.length
      ? randomProject.required_label_ids
      : availableLabelsIds.length > 0
        ? faker.helpers.arrayElements(
            availableLabelsIds,
            1 // !! Increasing this will produce bugs !!
            // 1 record avoids the potential of two (or more) label_ids belonging to the
            // same label.parent_id being chosen, which will throw validation errors on Timely API
            // PS: We don't have parent_id knowledge via ProjectLabel shape, so avoiding the complexity for ~no gain.
          )
        : [];

    const timely_label_ids = aiPreferences.train_tags ? sampleLabelIds : [];

    const inference: AiInference = {
      confidence: faker.number.float({ min: 0.75, max: 0.95 }),
      note: note,
      valid_as_hour: true,
      timely_project_id: randomProject.id,
      timely_label_ids,
    };

    return createMockAiPrediction({
      id: faker.string.uuid(),
      started_at: block.from,
      ended_at: block.to,
      created_at: block.from,
      inferred: inference,
      timely_user_id: faker.number.int({ min: 1, max: 1000 }),
      timely_account_id: faker.number.int({ min: 1, max: 100 }),
      user_id: faker.string.uuid(),
    });
  });

  return predictions;
};

export const adjustBlocksToSummaryBlocks = (
  memoryBlocks: Array<{ from: string; to: string; note: string }>,
  summaryBlocks: Array<MemoryBlock>
): Array<{ from: string; to: string; note: string }> => {
  // Return early if no memory blocks or summary blocks
  if (memoryBlocks.length === 0 || summaryBlocks.length === 0) {
    return memoryBlocks;
  }

  // Sort summary blocks by start time to ensure we process them in order
  const sortedSummaryBlocks = [...summaryBlocks].sort(
    (a, b) => a.startTime.toMillis() - b.startTime.toMillis()
  );

  // Convert memory blocks to DateTime for easier processing
  const processedMemoryBlocks = memoryBlocks.map(block => ({
    from: fromISO(block.from),
    to: fromISO(block.to),
    note: block.note,
  }));

  // Assign memory blocks to summary blocks, ensuring no gaps
  const result: Array<{ from: string; to: string; note: string }> = [];

  // First, find the best matching memory block for each summary block
  sortedSummaryBlocks.forEach(summaryBlock => {
    let bestMemBlockIndex = 0;
    let bestScore = -Infinity;

    processedMemoryBlocks.forEach((memBlock, memIndex) => {
      // Check if there's overlap
      const hasOverlap = !(
        memBlock.to < summaryBlock.startTime || memBlock.from > summaryBlock.endTime
      );

      let score;
      if (hasOverlap) {
        // Calculate overlap amount
        const overlapStart = DateTime.max(memBlock.from, summaryBlock.startTime);
        const overlapEnd = DateTime.min(memBlock.to, summaryBlock.endTime);
        score = overlapEnd.diff(overlapStart).milliseconds;
      } else {
        // Calculate negative distance to midpoint
        const memMidpoint = memBlock.from.plus({
          milliseconds: memBlock.to.diff(memBlock.from).milliseconds / 2,
        });
        const summaryMidpoint = summaryBlock.startTime.plus({
          milliseconds: summaryBlock.endTime.diff(summaryBlock.startTime).milliseconds / 2,
        });
        score = -Math.abs(summaryMidpoint.diff(memMidpoint).milliseconds);
      }

      if (score > bestScore) {
        bestScore = score;
        bestMemBlockIndex = memIndex;
      }
    });

    // Use the best matching memory block's note with this summary block's boundaries
    result.push({
      from: summaryBlock.startTime.toISO(),
      to: summaryBlock.endTime.toISO(),
      note: processedMemoryBlocks[bestMemBlockIndex].note,
    });
  });

  // Combine adjacent blocks that have the same note
  const combinedResult: Array<{ from: string; to: string; note: string }> = [];

  for (let i = 0; i < result.length; i++) {
    const currentBlock = result[i];

    // If this is the first block, add it
    if (i === 0) {
      combinedResult.push({ ...currentBlock });
      continue;
    }

    const previousBlock = combinedResult[combinedResult.length - 1];
    const currentFrom = DateTime.fromISO(currentBlock.from);
    const previousTo = DateTime.fromISO(previousBlock.to);

    // Check if blocks are adjacent (end time of previous = start time of current)
    // and have the same note
    if (currentFrom.equals(previousTo) && currentBlock.note === previousBlock.note) {
      // Extend the previous block's end time
      previousBlock.to = currentBlock.to;
    } else {
      // Otherwise add as a new block
      combinedResult.push({ ...currentBlock });
    }
  }

  return combinedResult;
};
